import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoute } from "wouter";
import { ArrowLeft } from "lucide-react";
import QRCode from "qrcode";
import { api, ApiError, formatBRL } from "../lib/api.js";
import { formatBillingDate } from "../lib/formatBillingDate.js";
import {
  completeIntegratedPaymentReturn,
  goToAutoLogin,
  isEmbeddedBillingContext,
} from "../lib/billingPostMessage.js";
import {
  delayAfterPaymentIdentified,
  PAYMENT_IDENTIFIED_DISPLAY_MS,
} from "../lib/paymentIdentifiedRedirect.js";
import { provisionAndGetLoginUrl } from "../lib/provisionAccess.js";
import { PaymentIdentifiedScreen } from "../components/PaymentIdentifiedScreen.js";
import { PreparingAccessScreen } from "../components/PreparingAccessScreen.js";
import { RenewalTerms } from "../components/RenewalTerms.js";
import type { PaymentMethod } from "../../../shared/types.js";
import { loadPersistedCheckoutOfferToken } from "../lib/checkoutOfferSession.js";
import {
  buildPixBackUrl,
  clearPixCheckoutContext,
  copyPixEmvToClipboard,
  ensureActivePixOrder,
  isPixOrderActive,
  loadPixCheckoutContext,
  loadStoredPixEmv,
  regeneratePixOrder,
  resolveIntegratedPixReturnUrl,
  resolvePixCheckoutContext,
  resolvePixEmv,
  savePixCheckoutContext,
  type PixCheckoutContext,
  type PixOrderSnapshot,
} from "../lib/pixCheckout.js";

async function renderQr(emv: string): Promise<string> {
  return QRCode.toDataURL(emv, { width: 320, margin: 1, errorCorrectionLevel: "M" });
}

export function PixPendingPage() {
  const { t, i18n } = useTranslation();
  const [, params] = useRoute<{ id: string }>("/pix/pending/:id");
  const [, setLocation] = useLocation();
  const orderId = params?.id;

  const [order, setOrder] = useState<PixOrderSnapshot | null>(null);
  const [checkoutCtx, setCheckoutCtx] = useState<PixCheckoutContext | null>(null);
  const [emv, setEmv] = useState<string>(() => (orderId ? loadStoredPixEmv(orderId) : ""));
  const [qrSrc, setQrSrc] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const emvTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [timeLeftMs, setTimeLeftMs] = useState(15 * 60 * 1000);
  const [recheckingNow, setRecheckingNow] = useState(false);
  const [recheckHint, setRecheckHint] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preparingPix, setPreparingPix] = useState(true);
  const [paymentIdentified, setPaymentIdentified] = useState(false);
  const [preparingAccess, setPreparingAccess] = useState(false);
  const [integratedReturnDone, setIntegratedReturnDone] = useState(false);
  const paymentHandledRef = useRef(false);
  const regeneratingRef = useRef(false);
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const onPaymentIdentified = useCallback(
    (r: PixOrderSnapshot) => {
      if (paymentHandledRef.current) return;
      paymentHandledRef.current = true;
      const integratedReturnUrl = resolveIntegratedPixReturnUrl(
        orderId ? loadPixCheckoutContext(orderId) ?? checkoutCtx : checkoutCtx,
        searchParams,
      );
      if (orderId) clearPixCheckoutContext(orderId);
      // Second payment on a used offer link: no access flow; the success page explains the refund.
      if (r.offer_already_redeemed) {
        setLocation(`/success?order=${r.id}&identified=1`);
        return;
      }
      setOrder(r);
      setPaymentIdentified(true);

      const integratedFlow = Boolean(integratedReturnUrl) || checkoutCtx?.returnTo === "integrated";
      delayAfterPaymentIdentified(
        () => {
          // New customer: provision the account (n8n) then auto-login into the app.
          if (r.status === "paid" && r.context_kind === "new_customer") {
            void (async () => {
              setPreparingAccess(true);
              const loginUrl = (await provisionAndGetLoginUrl(r.id)) ?? r.redirect ?? null;
              if (loginUrl) {
                if (r.plan && r.interval) {
                  goToAutoLogin({
                    loginUrl,
                    plan: r.plan,
                    interval: r.interval,
                    paymentMethod: r.payment_method as PaymentMethod | undefined,
                  });
                } else if (!isEmbeddedBillingContext()) {
                  window.location.href = loginUrl;
                }
                return;
              }
              setLocation(`/success?order=${r.id}&identified=1`);
            })();
            return;
          }
          if (r.status === "paid" && r.plan && r.interval && isEmbeddedBillingContext()) {
            completeIntegratedPaymentReturn({
              plan: r.plan,
              interval: r.interval,
              paymentMethod: r.payment_method as PaymentMethod | undefined,
              actionDelayMs: 0,
            });
            return;
          }
          if (r.redirect) {
            window.location.href = r.redirect;
            return;
          }
          if (r.status === "paid" && r.context_kind === "existing_customer" && r.plan && r.interval) {
            const result = completeIntegratedPaymentReturn({
              plan: r.plan,
              interval: r.interval,
              paymentMethod: r.payment_method as PaymentMethod | undefined,
              appReturnUrl: integratedReturnUrl ? null : r.app_return_url,
              integratedReturnUrl,
              actionDelayMs: 0,
            });
            if (result === "popup") return;
            if (result === "redirect") return;
            if (integratedReturnUrl) {
              window.location.href = integratedReturnUrl;
              return;
            }
            setIntegratedReturnDone(true);
            return;
          }
          setLocation(`/success?order=${r.id}&identified=1`);
        },
        integratedFlow ? 0 : PAYMENT_IDENTIFIED_DISPLAY_MS,
      );
    },
    [orderId, setLocation, checkoutCtx, searchParams],
  );
  const onPaymentIdentifiedRef = useRef(onPaymentIdentified);
  onPaymentIdentifiedRef.current = onPaymentIdentified;

  const applyEmv = useCallback(async (nextEmv: string) => {
    if (!nextEmv) return;
    setEmv(nextEmv);
    try {
      const src = await renderQr(nextEmv);
      setQrSrc(src);
      setLoadError(null);
    } catch {
      setLoadError("qr_render_failed");
    }
  }, []);

  const rememberCheckoutContext = useCallback(
    (data: PixOrderSnapshot) => {
      if (!orderId) return;
      const stored = loadPixCheckoutContext(orderId);
      const resolved = resolvePixCheckoutContext({
        orderId,
        searchParams,
        stored,
        order: data,
      });
      if (!resolved) return;
      savePixCheckoutContext(resolved);
      setCheckoutCtx((prev) => {
        if (
          prev &&
          prev.orderId === resolved.orderId &&
          prev.plan === resolved.plan &&
          prev.interval === resolved.interval &&
          prev.email === resolved.email &&
          prev.returnTo === resolved.returnTo &&
          prev.integratedToken === resolved.integratedToken &&
          prev.offerToken === resolved.offerToken
        ) {
          return prev;
        }
        return resolved;
      });
    },
    [orderId, searchParams],
  );

  // Initial load once per orderId — do not depend on callbacks that change when
  // checkoutCtx updates, or this effect re-fetches in a loop.
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;

    const cached = orderId ? loadStoredPixEmv(orderId) : "";
    if (cached) {
      void applyEmv(cached);
    }
    const cachedCtx = loadPixCheckoutContext(orderId);
    if (cachedCtx) setCheckoutCtx(cachedCtx);
    else {
      const fromUrl = resolvePixCheckoutContext({
        orderId,
        searchParams,
        stored: null,
        order: null,
      });
      if (fromUrl) setCheckoutCtx(fromUrl);
    }

    void (async () => {
      setPreparingPix(true);
      try {
        let data = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
        if (cancelled) return;
        if (
          (data.status === "pending" || (data.status === "expired" && data.context_kind === "new_customer")) &&
          data.payment_method === "openpix" &&
          !isPixOrderActive(data)
        ) {
          data = await ensureActivePixOrder(orderId, { reviveExpired: true });
        }
        if (cancelled) return;
        setOrder(data);
        rememberCheckoutContext(data);
        if (data.pix_emv) {
          sessionStorage.setItem(`pix_emv_${orderId}`, data.pix_emv);
          await applyEmv(data.pix_emv);
        } else if (!cached) {
          setLoadError("missing_emv");
        }
        if (data.pix_expires_at) {
          setTimeLeftMs(Math.max(0, new Date(data.pix_expires_at).getTime() - Date.now()));
        }
        if (data.status === "paid") {
          onPaymentIdentifiedRef.current(data);
        }
      } catch (err) {
        if (cancelled) return;
        // Paid since, by card or on another order: no QR, and say so.
        if (err instanceof ApiError && (err.body as { error?: string } | null)?.error === "paid_elsewhere") {
          setQrSrc("");
          setLoadError("paid_elsewhere");
        } else if (!cached) {
          setLoadError("fetch_failed");
        }
      } finally {
        if (!cancelled) setPreparingPix(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally once per orderId
  }, [orderId]);

  /** Cheap DB read — webhooks usually mark paid without hitting OpenPix. */
  const pollOrderStatus = useCallback(async () => {
    if (!orderId || paymentHandledRef.current) return;
    try {
      const r = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
      setOrder(r);
      if (r.status === "paid") {
        onPaymentIdentifiedRef.current(r);
      }
    } catch {
      /* keep polling */
    }
  }, [orderId]);

  /** Ask OpenPix then re-read — used on focus / "Já paguei", not on every poll tick.
   *  Returns true when payment was identified. */
  const recheckWithProvider = useCallback(async (): Promise<boolean> => {
    if (!orderId || paymentHandledRef.current) return false;
    try {
      await api(`/api/public/orders/${orderId}/recheck`, { method: "POST" });
      const r = await api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
      setOrder(r);
      if (r.status === "paid") {
        onPaymentIdentifiedRef.current(r);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [orderId]);

  // Poll local order status every 10s (1 GET). Provider sync only on visibility / button.
  useEffect(() => {
    if (!orderId || paymentHandledRef.current) return;
    const handle = setInterval(() => void pollOrderStatus(), 10000);
    return () => clearInterval(handle);
  }, [orderId, pollOrderStatus]);

  useEffect(() => {
    if (!orderId || paymentHandledRef.current) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void recheckWithProvider();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [orderId, recheckWithProvider]);

  // Countdown timer.
  useEffect(() => {
    const h = setInterval(() => setTimeLeftMs((ms) => Math.max(0, ms - 1000)), 1000);
    return () => clearInterval(h);
  }, []);

  // When the charge expires on this screen, mint a fresh 15-minute PIX automatically.
  useEffect(() => {
    if (!orderId || paymentIdentified || order?.status !== "pending" || timeLeftMs > 0) return;
    if (regeneratingRef.current) return;
    regeneratingRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const r = await regeneratePixOrder(orderId);
        if (cancelled) return;
        sessionStorage.setItem(`pix_emv_${orderId}`, r.pix_emv);
        await applyEmv(r.pix_emv);
        setTimeLeftMs(Math.max(0, new Date(r.pix_expires_at).getTime() - Date.now()));
        if (!cancelled) {
          setOrder((prev) =>
            prev
              ? { ...prev, pix_emv: r.pix_emv, pix_expires_at: r.pix_expires_at }
              : prev,
          );
        }
      } catch {
        if (!cancelled) setLoadError("missing_emv");
      } finally {
        regeneratingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, order?.status, timeLeftMs, paymentIdentified, applyEmv]);

  const minutes = Math.floor(timeLeftMs / 60000);
  const seconds = Math.floor((timeLeftMs % 60000) / 1000);
  const expired = timeLeftMs <= 0;

  const timeStr = useMemo(
    () => `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
    [minutes, seconds],
  );

  const isMonthly = order?.interval !== "annual";
  const isDeferredAuth =
    order != null && order.final_cents === 0 && !!order.first_charge_at;
  const displayEmv = orderId ? resolvePixEmv(orderId, emv, order) : emv;
  const howToSteps = useMemo(
    () => [
      t("pix.howTo.step1"),
      t("pix.howTo.step2"),
      t("pix.howTo.step3"),
      isDeferredAuth
        ? t("pix.howTo.step4AuthorizeOnly")
        : isMonthly
          ? t("pix.howTo.step4Monthly")
          : t("pix.howTo.step4Annual"),
    ],
    [t, isMonthly, isDeferredAuth],
  );

  const checkoutBackUrl = useMemo(() => {
    if (checkoutCtx) return buildPixBackUrl(checkoutCtx);
    const integratedReturnUrl = resolveIntegratedPixReturnUrl(null, searchParams);
    if (integratedReturnUrl) return integratedReturnUrl;
    if (order?.plan && order.interval && order.email && order.context_kind !== "existing_customer") {
      return buildPixBackUrl({
        orderId: orderId!,
        plan: order.plan,
        interval: order.interval,
        email: order.email,
        offerToken: loadPersistedCheckoutOfferToken() || undefined,
      });
    }
    return "/plans";
  }, [checkoutCtx, order, orderId, searchParams]);

  async function regenerate() {
    if (!orderId) return;
    setLoadError(null);
    const r = await regeneratePixOrder(orderId);
    sessionStorage.setItem(`pix_emv_${orderId}`, r.pix_emv);
    await applyEmv(r.pix_emv);
    setTimeLeftMs(Math.max(0, new Date(r.pix_expires_at).getTime() - Date.now()));
  }

  async function recheck() {
    if (!orderId || paymentHandledRef.current) return;
    setRecheckingNow(true);
    setRecheckHint(false);
    try {
      const paidNow = await recheckWithProvider();
      if (!paidNow && !paymentHandledRef.current) {
        setRecheckHint(true);
      }
    } finally {
      setRecheckingNow(false);
    }
  }

  if (preparingAccess) {
    return <PreparingAccessScreen />;
  }

  if (paymentIdentified) {
    if (integratedReturnDone) {
      return (
        <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
          <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
            <div className="text-2xl font-semibold mb-3">{t("success.integratedTitle")}</div>
          </div>
        </main>
      );
    }
    return <PaymentIdentifiedScreen />;
  }

  // Paid since, by card or on another order: nothing to pay here, so no QR, timer or how-to.
  if (loadError === "paid_elsewhere") {
    return (
      <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-6 text-[color:var(--text-primary)]">
          <h1 className="text-xl font-semibold">{t("pix.paidElsewhereTitle")}</h1>
          <p className="mt-2 text-sm text-ink-2">{t("pix.paidElsewhere")}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
      <div className="card-glass max-w-md w-full p-6 text-[color:var(--text-primary)]">
        <button
          type="button"
          onClick={() => setLocation(checkoutBackUrl)}
          className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("pix.backToCheckout")}
        </button>

        <h1 className="text-xl font-semibold">{t("pix.title", { minutes: 15 })}</h1>
        <div className="mt-2 flex items-center justify-between text-sm text-ink-2">
          <span>{order?.status === "paid" ? t("pix.paid") : t("pix.scanQrCode")}</span>
          <span className={`tabular-nums ${expired ? "text-danger" : "text-purple-deep"}`}>{timeStr}</span>
        </div>

        {order?.status !== "paid" && (
          <section className="pix-howto mt-4" aria-label={t("pix.howTo.title")}>
            <h2 className="pix-howto__title">{t("pix.howTo.title")}</h2>
            <ol className="pix-howto__steps">
              {howToSteps.map((step, i) => (
                <li key={i} className="pix-howto__step">
                  <span className="pix-howto__num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <p className="pix-howto__note">
              {isDeferredAuth
                ? t("pix.howTo.noteDeferred")
                : isMonthly
                  ? t("pix.howTo.noteMonthly")
                  : t("pix.howTo.noteAnnual")}
            </p>
          </section>
        )}

        {order?.status !== "paid" &&
          !isDeferredAuth &&
          order?.context_kind === "new_customer" &&
          (order.final_cents ?? 0) > 0 &&
          order.interval && (
            <RenewalTerms
              todayCents={order.final_cents ?? 0}
              renewalCents={order.renewal_cents ?? order.recurring_cents ?? order.final_cents ?? 0}
              interval={order.interval}
              nextChargeAt={order.first_charge_at}
            />
          )}

        {order?.status !== "paid" &&
          order?.context_kind !== "new_customer" &&
          (isDeferredAuth || (order?.final_cents ?? 0) > 0) && (
          <section
            className="mt-4 rounded-lg border border-purple-deep/25 bg-purple-deep/5 p-4 text-sm"
            aria-label={t("pix.billingSchedule.title")}
          >
            <h2 className="font-medium text-purple-deep">{t("pix.billingSchedule.title")}</h2>
            {isDeferredAuth && order.first_charge_at ? (
              <>
                <p className="mt-1 text-ink-2">
                  {isMonthly
                    ? t("pix.billingSchedule.authorizeOnlyMonthly")
                    : t("pix.billingSchedule.authorizeOnlyAnnual")}
                </p>
                <dl className="mt-3 space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-2">{t("pix.billingSchedule.firstCharge")}</dt>
                    <dd className="font-medium text-right">
                      {formatBillingDate(order.first_charge_at, i18n.language)}
                    </dd>
                  </div>
                  {order.recurring_cents != null && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-ink-2">
                        {isMonthly
                          ? t("pix.billingSchedule.recurringMonthly")
                          : t("pix.billingSchedule.recurringAnnual")}
                      </dt>
                      <dd className="font-medium">{formatBRL(order.recurring_cents)}</dd>
                    </div>
                  )}
                </dl>
              </>
            ) : (
              <>
                <p className="mt-1 font-medium">
                  {t("pix.billingSchedule.chargeToday", {
                    amount: formatBRL(order?.final_cents ?? 0),
                  })}
                </p>
                {order?.first_charge_at &&
                  new Date(order.first_charge_at).getTime() > Date.now() + 86_400_000 && (
                    <p className="mt-2 text-ink-2">
                      {t("pix.billingSchedule.nextRenewal", {
                        date: formatBillingDate(order.first_charge_at, i18n.language),
                      })}
                    </p>
                  )}
              </>
            )}
          </section>
        )}

        <div className="mt-4 flex items-center justify-center">
          {qrSrc ? (
            <img src={qrSrc} alt="PIX QR" className="rounded-md w-64 h-64 bg-white p-2" />
          ) : (
            <div className="w-64 h-64 bg-black/5 rounded-md flex flex-col items-center justify-center text-ink-2 text-sm text-center px-4 gap-2">
              {loadError === "missing_emv" ? (
                <>
                  <span>{t("pix.missingEmv")}</span>
                  <button type="button" className="btn-ghost text-xs" onClick={regenerate}>
                    {t("pix.regenerate")}
                  </button>
                </>
              ) : preparingPix ? (
                <>
                  <span className="font-medium">{t("integrated.openingPaymentHint")}</span>
                  <span className="text-xs">{t("common.loading")}</span>
                </>
              ) : (
                <span>{t("common.loading")}</span>
              )}
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="label mb-1">{t("pix.copyCode")}</div>
          <textarea
            ref={emvTextareaRef}
            readOnly
            className="input font-mono text-xs"
            rows={3}
            value={displayEmv}
          />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              className="btn-ghost flex-1"
              onClick={async () => {
                if (!orderId) return;
                const code = resolvePixEmv(orderId, emv, order);
                const ok = await copyPixEmvToClipboard(code, emvTextareaRef.current);
                if (!ok) return;
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? t("pix.copied") : t("pix.copy")}
            </button>
            <button type="button" className="btn-primary flex-1" onClick={recheck} disabled={recheckingNow}>
              {recheckingNow ? t("pix.checking") : t("pix.alreadyPaid")}
            </button>
          </div>
          {recheckHint && (
            <div
              className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-[color:var(--text-primary)]"
              role="status"
            >
              {t("pix.notIdentifiedYet")}
            </div>
          )}
        </div>

        {(expired || loadError === "missing_emv") && qrSrc && (
          <div className="mt-4 p-3 rounded-md bg-danger/10 border border-danger/30 text-sm">
            <div className="font-medium text-danger">{t("pix.expired")}</div>
            <button type="button" className="btn-ghost mt-2" onClick={regenerate}>
              {t("pix.regenerate")}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
